import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { howItWorksContent, images } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

const HowItWorks = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.how-animate',
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 bg-[#0B1121]">
      <div className="l-container">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between gap-8 mb-16 how-animate">
          <p className="sub-title">{howItWorksContent.label}</p>
          <h2 className="big-title">
            {howItWorksContent.title}{' '}
            <span className="text-[#4ECDC4]">{howItWorksContent.titleHighlight}</span>
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex flex-col lg:flex-row gap-16 mb-20 how-animate">
          {howItWorksContent.items.map((item, index) => (
            <div
              key={index}
              className="flex-1 cursor-pointer"
            >
              <span className="text-[#4ECDC4] text-sm font-medium block mb-4">
                {item.label}
              </span>
              <p className="text-white/80 leading-relaxed">
                {item.text}
              </p>
            </div>
          ))}
        </div>

        {/* Products - side by side on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 how-animate">
          {howItWorksContent.products.map((product, index) => (
            <ProductCard 
              key={index}
              product={product}
              image={index === 0 ? images.howH2 : images.howGraphite}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Product Card - single title that stays at bottom, additional text reveals on hover
const ProductCard = ({ product, image }) => {
  return (
    <div className="relative overflow-hidden rounded-lg cursor-pointer group">
      {/* Background Image with blur on hover */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={image}
          alt={product.title}
          className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 group-hover:blur-sm"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/70 transition-all duration-500" />
      </div>

      {/* Content container - positioned at bottom */}
      <div className="absolute left-0 right-0 bottom-0 p-6 lg:p-8">
        {/* Additional text - hidden by default, revealed on hover */}
        <div 
          className="overflow-hidden transition-all duration-500 ease-out"
          style={{ maxHeight: '0px' }}
        >
          <div className="pb-3">
            <p className="text-[#4ECDC4] text-sm mb-2">
              {product.subtitle}
            </p>
            <p className="text-white/80 text-sm leading-relaxed">
              {product.description}
            </p>
          </div>
        </div>
        
        {/* Title - always visible at bottom */}
        <h4 className="font-serif text-2xl lg:text-3xl text-white">
          {product.title}
        </h4>
      </div>

      {/* CSS for hover state */}
      <style>{`
        .group:hover .overflow-hidden[style] {
          max-height: 200px !important;
        }
      `}</style>
    </div>
  );
};

export default HowItWorks;
